export const US_STATE_NAMES: Record<string, string> = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  DC: "District of Columbia",
  PR: "Puerto Rico",
};

const US_STATE_CODES_BY_NAME: Record<string, string> = Object.fromEntries(
  Object.entries(US_STATE_NAMES).map(([code, name]) => [
    name.toLowerCase(),
    code,
  ]),
);

const ZIP_PREFIX_STATES: Array<{ min: number; max: number; state: string }> = [
  { min: 5, max: 5, state: "NY" },
  { min: 6, max: 9, state: "PR" },
  { min: 10, max: 27, state: "MA" },
  { min: 28, max: 29, state: "RI" },
  { min: 30, max: 38, state: "NH" },
  { min: 39, max: 49, state: "ME" },
  { min: 55, max: 55, state: "MA" },
  { min: 50, max: 59, state: "VT" },
  { min: 60, max: 69, state: "CT" },
  { min: 70, max: 89, state: "NJ" },
  { min: 100, max: 149, state: "NY" },
  { min: 150, max: 196, state: "PA" },
  { min: 197, max: 199, state: "DE" },
  { min: 200, max: 200, state: "DC" },
  { min: 201, max: 201, state: "VA" },
  { min: 202, max: 205, state: "DC" },
  { min: 206, max: 219, state: "MD" },
  { min: 220, max: 246, state: "VA" },
  { min: 247, max: 268, state: "WV" },
  { min: 270, max: 289, state: "NC" },
  { min: 290, max: 299, state: "SC" },
  { min: 300, max: 319, state: "GA" },
  { min: 320, max: 349, state: "FL" },
  { min: 350, max: 369, state: "AL" },
  { min: 370, max: 385, state: "TN" },
  { min: 386, max: 397, state: "MS" },
  { min: 398, max: 399, state: "GA" },
  { min: 400, max: 427, state: "KY" },
  { min: 430, max: 459, state: "OH" },
  { min: 460, max: 479, state: "IN" },
  { min: 480, max: 499, state: "MI" },
  { min: 500, max: 528, state: "IA" },
  { min: 530, max: 549, state: "WI" },
  { min: 550, max: 567, state: "MN" },
  { min: 570, max: 577, state: "SD" },
  { min: 580, max: 588, state: "ND" },
  { min: 590, max: 599, state: "MT" },
  { min: 600, max: 629, state: "IL" },
  { min: 630, max: 658, state: "MO" },
  { min: 660, max: 679, state: "KS" },
  { min: 680, max: 693, state: "NE" },
  { min: 700, max: 714, state: "LA" },
  { min: 716, max: 729, state: "AR" },
  { min: 730, max: 749, state: "OK" },
  { min: 750, max: 799, state: "TX" },
  { min: 800, max: 816, state: "CO" },
  { min: 820, max: 831, state: "WY" },
  { min: 832, max: 838, state: "ID" },
  { min: 840, max: 847, state: "UT" },
  { min: 850, max: 865, state: "AZ" },
  { min: 870, max: 884, state: "NM" },
  { min: 885, max: 885, state: "TX" },
  { min: 889, max: 898, state: "NV" },
  { min: 900, max: 961, state: "CA" },
  { min: 967, max: 968, state: "HI" },
  { min: 970, max: 979, state: "OR" },
  { min: 980, max: 994, state: "WA" },
  { min: 995, max: 999, state: "AK" },
];

export function normalizeUSStateCode(value: string | null | undefined) {
  const token = value?.trim().replace(/\.$/, "");
  if (!token) return null;

  const upper = token.toUpperCase();
  if (/^[A-Z]{2}$/.test(upper) && US_STATE_NAMES[upper]) {
    return upper;
  }
  return US_STATE_CODES_BY_NAME[token.toLowerCase()] ?? null;
}

export function displayUSStateName(state: string | null | undefined) {
  if (!state) return "State";
  return US_STATE_NAMES[state] ?? state;
}

export function extractUSStateCodeFromLocation(
  value: string | null | undefined,
) {
  const text = value?.trim().replace(/\s+/g, " ");
  if (!text) return null;

  const direct = normalizeUSStateCode(text);
  if (direct) return direct;

  const prefixedCode = text.match(/^([A-Za-z]{2})(?:[-_]|$)/);
  if (prefixedCode) {
    const state = normalizeUSStateCode(prefixedCode[1]);
    if (state) return state;
  }

  const commaParts = text
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  for (let i = commaParts.length - 1; i >= 0; i -= 1) {
    const state = normalizeUSStateCode(stripPostalAndCountry(commaParts[i]));
    if (state) return state;
  }

  const stateBeforeZIP = text.match(/\b([A-Za-z]{2})\s+\d{5}(?:-\d{4})?\b/i);
  if (stateBeforeZIP) {
    const state = normalizeUSStateCode(stateBeforeZIP[1]);
    if (state) return state;
  }

  const terminalCode = text.match(/\b([A-Za-z]{2})\.?$/);
  if (terminalCode) {
    const state = normalizeUSStateCode(terminalCode[1]);
    if (state) return state;
  }

  const zip = text.match(/\b\d{5}(?:-\d{4})?\b/);
  return zip ? stateFromZIP(zip[0]) : null;
}

function stripPostalAndCountry(value: string) {
  return value
    .replace(/\b\d{5}(?:-\d{4})?\b/g, "")
    .replace(/\b(?:USA|US|United States)\b/gi, "")
    .trim();
}

function stateFromZIP(value: string) {
  const prefix = Number.parseInt(value.slice(0, 3), 10);
  if (Number.isNaN(prefix)) return null;
  return (
    ZIP_PREFIX_STATES.find(
      (entry) => prefix >= entry.min && prefix <= entry.max,
    )?.state ?? null
  );
}
